import React, { useRef } from 'react';
import { ChevronRightIcon } from '@fronton/icons-react';
import cn from 'clsx';
import { Button, Link, SectionSubTitle, SwiperSlide, Swiper, Typography } from 'common/components';
import { useIntersection } from 'common/hooks';
import { createElementKey } from 'utils';
import { analyticsUtils } from 'utils/analytics-utils';
import {
  seasonStartCollections,
  seasonStartCollectionsHeaderWrapper,
  seasonStartCollectionsMoreLink,
  seasonStartCollectionsTitle,
  seasonStartCollectionImage,
  seasonStartCollectionSlide,
  seasonStartCollectionSlideInfo,
  seasonStartCollectionSwiper,
  seasonStartCollectionsMoreText,
  seasonStartCollectionsMoreTextMobile,
} from './season-start-collections-styles';

type SeasonStartCollection = {
  discount: number;
  productId: number;
  title: string;
  description: string;
  img: {
    src: string;
    alt: string;
  },
  href: string;
};

type SeasonStartCollectionsProps = {
  elementBlock: string;
  division: string;
  subdivision: string;
  sliderClassName?: string;
  showAllLink: {
    href: string;
    title: string;
  };
  collectionList: SeasonStartCollection[];
}

export const SeasonStartCollections: React.FC<SeasonStartCollectionsProps> = (
  { elementBlock, division, subdivision, sliderClassName, showAllLink, collectionList }
) => {
  const productRef = useRef<HTMLAnchorElement[]>([]);

  useIntersection(productRef, el => {
    const position = el.getAttribute('data-position');

    if (!position) {
      return;
    }

    const numberPosition = Number(position);
    const collection = collectionList[numberPosition];

    analyticsUtils.sendEvent('GA:impressions', {
      elementType: 'product',
      elementBlock,
      division,
      subdivision,
      komplektyId: collection.productId,
      productName: collection.title,
      productId: collection.productId,
      productPrice: null,
      productBelong: 'LeroyMerlin',
      productTag: undefined,
      productDiscount: collection.discount,
      productPrevPrice: null,
      productPosition: numberPosition + 1,
    });
  });

  const handleCardClick = (collection: SeasonStartCollection, position: number) => {
    analyticsUtils.sendEvent('GA:clickProduct', {
      elementType: 'product',
      elementBlock,
      division,
      subdivision,
      komplektyId: collection.productId,
      productName: collection.title,
      productId: collection.productId,
      productPrice: null,
      productBelong: 'LeroyMerlin',
      productTag: undefined,
      productDiscount: collection.discount,
      productPrevPrice: null,
      productPosition: position,
    });
  };

  const handleCardnWheelClick = (e: MouseEvent, collection: SeasonStartCollection, position: number) => {
    if (e.button === 1) {
      handleCardClick(collection, position);
    }
  };

  const handleSeeMoreLinkClick = () => {
    analyticsUtils.linkClickButtonEvent(elementBlock, 'Готовые комплекты', 'Смотреть все', showAllLink.href);
  }

  const handleSeeMoreLinkWheelClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Проверка, что кликнута именно средняя кнопка/колёсико
    if (e.button === 1) {
      analyticsUtils.linkClickButtonEvent(elementBlock, 'Готовые комплекты', 'Смотреть все', showAllLink.href);
    }
  };

  return (
    <div className={seasonStartCollections}>
      <div className={seasonStartCollectionsHeaderWrapper}>
        <SectionSubTitle className={seasonStartCollectionsTitle} margin="none" color="text-invert">
          Готовые комплекты
        </SectionSubTitle>
        <Link
          className={seasonStartCollectionsMoreLink}
          onClick={handleSeeMoreLinkClick}
          onAuxClick={handleSeeMoreLinkWheelClick}
          {...showAllLink}
        >
          <Typography
            className={seasonStartCollectionsMoreText}
            variant="s"
            size="body_accent"
            color='text-invert'
          >
            Смотреть все
          </Typography>
          <Typography
            className={seasonStartCollectionsMoreTextMobile}
            variant="s"
            size="body_accent"
            color='text-invert'
          >
            Все
          </Typography>
          <ChevronRightIcon type="outline" size="m" color="text-invert" />
        </Link>
      </div>

      <Swiper
        breakpoints={{
          320: { enabled: true },
          728: { enabled: false },
        }}
        className={cn(seasonStartCollectionSwiper, sliderClassName)}
        slidesPerView="auto"
        watchOverflow
      >
        {collectionList.map((collection, index) => (
          <SwiperSlide className={seasonStartCollectionSlide} key={createElementKey('bathroom-collection', index)}>
            <Link
              refCb={(el: HTMLAnchorElement | null) => {
                const position = el?.getAttribute('data-position');
                if (position && el) {
                  productRef.current[+position] = el;
                }
              }}
              data-position={index}
              href={collection.href}
              /* @ts-expect-error: param typecheck error */
              onAuxClick={e => handleCardnWheelClick(e, collection, index + 1)}
              onClick={() => handleCardClick(collection, index + 1)}
              key={createElementKey('bathroom-collection', index)}
              title={collection.title}
            >
              <img alt={collection.img.alt} className={seasonStartCollectionImage} src={collection.img.src} />
              <div className={seasonStartCollectionSlideInfo}>
                <Typography variant="s" size="subtitle" color="text-invert">
                  {collection.title}
                </Typography>
                <Typography variant="minor_caption" color="text-secondary-invert">
                  {collection.description}
                </Typography>
              </div>
              <Button invert variant="secondary" size="m" wide>
                Подробнее
              </Button>
            </Link>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
